import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { inquiryAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

// 스타일드 컴포넌트
const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.5rem;
  }
`;

const PageDescription = styled.p`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const Form = styled.form`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.25rem;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  min-height: 250px;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.25rem;
  font-size: 1rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.border};
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: white;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    order: 2;
  }
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  margin: 1rem 0;
  background-color: ${({ theme }) => theme.colors.danger};
  color: white;
  border-radius: 0.25rem;
  font-size: 0.9rem;
`;

const FieldErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

interface ValidationError {
  type: string;
  value: string;
  msg: string;
  path: string;
  location: string;
}

const InquiryNewPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  
  // 로그인 확인
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/inquiry/new' } });
    }
  }, [isAuthenticated, navigate]);
  
  // 문의 작성 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim()) {
      setError('제목과 내용을 모두 입력해주세요.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      setFieldErrors({});
      
      const response = await inquiryAPI.createInquiry(
        title.trim(),
        content.trim()
      );
      
      if (response.data.success) {
        navigate(`/inquiry/${response.data.inquiry.inquiry_id}`);
      } else {
        if (response.data.errors && Array.isArray(response.data.errors)) {
          const errors: {[key: string]: string} = {};
          response.data.errors.forEach((err: ValidationError) => {
            errors[err.path] = err.msg;
          });
          setFieldErrors(errors);
          
          if (Object.keys(errors).length > 0) {
            setError('입력 내용을 확인해주세요.');
          } else {
            throw new Error(response.data.message || '문의 작성에 실패했습니다.');
          }
        } else {
          throw new Error(response.data.message || '문의 작성에 실패했습니다.');
        }
      }
    } catch (err : any) {
      let response = err.response || { data:{}};
      if (response.data.errors && Array.isArray(response.data.errors)) {
        const errors: {[key: string]: string} = {};
        response.data.errors.forEach((err: ValidationError) => {
          errors[err.path] = err.msg;
        });
        setFieldErrors(errors);
        
        if (Object.keys(errors).length > 0) {
          setError('입력 내용을 확인해주세요.');
        } else {
          setError(response.data.message || '문의 작성에 실패했습니다.');
        }
      } else {
        setError(response.data.message || '문의 작성에 실패했습니다.');
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  // 취소 핸들러
  const handleCancel = () => {
    navigate('/inquiry');
  };
  
  return (
    <Container>
      <PageHeader>
        <PageTitle>새 문의 작성</PageTitle>
        <PageDescription>문의사항이나 요청사항을 작성하시면 빠르게 답변 드리겠습니다.</PageDescription>
      </PageHeader>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <FormLabel htmlFor="title">제목</FormLabel>
          <FormInput
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="문의 제목을 입력해주세요"
            required
          />
          {fieldErrors.title && <FieldErrorMessage>{fieldErrors.title}</FieldErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <FormLabel htmlFor="content">내용</FormLabel>
          <FormTextarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="문의하실 내용을 자세히 작성해 주세요"
            required
          />
          {fieldErrors.content && <FieldErrorMessage>{fieldErrors.content}</FieldErrorMessage>}
        </FormGroup>
        
        <ButtonContainer>
          <CancelButton type="button" onClick={handleCancel}>
            취소
          </CancelButton>
          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? '제출 중...' : '문의 제출하기'}
          </SubmitButton>
        </ButtonContainer>
      </Form>
    </Container>
  );
};

export default InquiryNewPage; 